import { useCallback, useEffect, useState } from "react";
import NewVideoPlayer from '../components/NewVideoPlayer';
import ParentToolsModal from '../components/ParentToolsModal';
import '../App.css';
import '../FeaturedAudioPlayer.css';
import seriesJsonData from './SeriesData.json'
import { TextExpansionProvider } from '../contexts/TextExpansionContext';
import { useBrand } from '../contexts/BrandContext';

const DEFAULT_POSTROLL_AD_URL = window.broadcastPlayerData?.postrollAdUrl || 'https://insight.org/media/xml/vast-chuck-jeffress.php';
// No hardcoded fallback: when the Pause Ad Image URL is empty in the WP settings, the
// pause ad does not render at all (NewVideoPlayer gates the overlay on a truthy pauseAdUrl).
// This prevents one brand's creative (e.g. an IFL ad) from appearing on another (e.g. Paws & Tales).
const DEFAULT_PAUSE_AD_URL = window.broadcastPlayerData?.pauseAdImageUrl || '';
const DEFAULT_PAUSE_AD_CLICK_URL = window.broadcastPlayerData?.pauseAdClickUrl || 'https://give.insight.org/donate/give?source=WEBMCPAD';

function SeriesPlayer({ prerollAdEnabled = false, postrollAdEnabled = true }) {
    const {
        translations: t,
        isVPVBranded,
        isPawsAndTales,
        getThemeClass,
        customColor,
        textColor
    } = useBrand();

    const [error, setError] = useState(null);
    const [playlistItems, setPlaylistItems] = useState(null);
    const [initialIndex, setInitialIndex] = useState(0);
    const [currentPlaylistItem, setCurrentPlaylistItem] = useState(null);

    const loadSeriesData = useCallback(() => {
        try {
            const queryParams = new URLSearchParams(window.location.search);
            const mid = queryParams.get('mid');
            
            const data = window.broadcastPlayerData?.seriesJSON || seriesJsonData;
            console.log('seriesJSON:', data);

            const items = data.map(item => {
                let sources = item.sources || [];
                if (sources.length === 0) {
                    const getMimeType = (url, defaultType) => {
                        if (!url) return defaultType;
                        if (url.includes('.m3u8')) return 'application/x-mpegURL';
                        if (url.includes('.mp4')) return 'video/mp4';
                        if (url.includes('.mp3')) return 'audio/mpeg';
                        return defaultType;
                    };

                    if (item['ifl:mp3file']) {
                        sources.push({ src: item['ifl:mp3file'], type: getMimeType(item['ifl:mp3file'], 'audio/mpeg') });
                    }
                    if (item['ifl:videofile']) {
                        sources.push({ src: item['ifl:videofile'], type: getMimeType(item['ifl:videofile'], 'video/mp4') });
                    }
                    if (item['ifl:mp4file']) {
                        sources.push({ src: item['ifl:mp4file'], type: getMimeType(item['ifl:mp4file'], 'video/mp4') });
                    }
                }

                const posterUrl = item.poster || item['ifl:messageImage'] || item['ifl:seriesImage'] || '';

                // Decode HTML entities (e.g. &#8217; → ')
                const decodeEntities = (str) => {
                    if (!str) return '';
                    const txt = document.createElement('textarea');
                    txt.innerHTML = str;
                    return txt.value;
                };

                return {
                    ...item,
                    sources: sources,
                    name: decodeEntities(item.name || item['ifl:title']),
                    poster: posterUrl,
                    thumbnail: [{ src: posterUrl, class: 'vjs-playlist-thumbnail' }],
                    seriesName: item.seriesName || item['ifl:subtitle'] || '',
                    seriesDateRange: item.seriesDateRange || item['ifl:seriesDateRange'] || '',
                    postrollAdUrl: item.postrollAdUrl || item['ifl:adSyncUrl'] || DEFAULT_POSTROLL_AD_URL,
                    messageNumber: item.messageNumber || item['ifl:messageNumber'] || '',
                    messageSpeaker: item.messageSpeaker || item['ifl:speaker'] || '',
                    playerType: item.playerType || item['ifl:playerType'] || '',
                    messageSummary: item.messageSummary || item['ifl:summary'] || '',
                    seriesSummary: item.seriesSummary || item['ifl:seriesSummary'] || '',
                    // Club-member-only fields: the theme emits them in SeriesJSON only for
                    // logged-in members, so absence = show the join CTA (see ParentToolsModal).
                    parentToolPdf: item.parentToolPdf || item['ifl:parentToolPDF'] || '',
                    activitySheetPdf: item.activitySheetPdf || item['ifl:activitySheetPDF'] || '',
                    scriptureReference: item.scriptureReference || item['ifl:scripture_reference'] || '',
                    notes: item.notes || item['ifl:notesURL'] || '',
                    message: item.message || item['ifl:messageURL'] || '',
                    series: item.series || item['ifl:seriesURL'] || '',
                    seriesPage: item['ifl:seriesPage'] || item.seriesPage || item['ifl:permalink'] || '',
                    messagePermalink: item.messagePermalink || item['ifl:permalink'] || '',
                    mediaType: (item['ifl:videofile'] || item['ifl:mp4file'] || (sources.length > 0 && sources[0].type.startsWith('video/'))) ? 'video' : 'audio'
                };
            });

            let startIndex = 0;
            if (mid) {
                const index = items.findIndex(item => (item.guid === mid || item['ifl:guid'] == mid));
                if (index !== -1) startIndex = index;
            }

            setPlaylistItems(items);
            setInitialIndex(startIndex);
            setCurrentPlaylistItem(items[startIndex]);
        } catch (err) {
            setError(new Error('Failed to load series data: ' + err.message));
        }
    }, []);

    useEffect(() => {
        loadSeriesData();
    }, [loadSeriesData]);

    useEffect(() => {
        if (currentPlaylistItem?.seriesName) {
            document.title = `${currentPlaylistItem.seriesName} | Insight for Living`;
        }
    }, [currentPlaylistItem]);

    const handlePlaylistItemChange = (item) => {
        setCurrentPlaylistItem(item);
    };

    // The Tools For Parents pill lives in imperative Video.js playlist DOM
    // (NewVideoPlayer.addDescriptionsToPlaylist), so it raises a CustomEvent with the
    // item's guid rather than calling into React directly.
    const [parentToolsGuid, setParentToolsGuid] = useState(null);

    useEffect(() => {
        const onOpen = (e) => setParentToolsGuid(e.detail?.guid ?? null);
        window.addEventListener('pnt:parent-tools-open', onOpen);
        return () => window.removeEventListener('pnt:parent-tools-open', onOpen);
    }, []);

    const parentToolsIndex = playlistItems && parentToolsGuid !== null
        ? playlistItems.findIndex((i) => (i.guid ?? i['ifl:guid']) === parentToolsGuid)
        : -1;
    const parentToolsItem = parentToolsIndex >= 0 ? playlistItems[parentToolsIndex] : null;

    return (
        <TextExpansionProvider>
            <div className={`media-player-root ${getThemeClass()} ${isVPVBranded ? 'vpv-branded' : ''}`}>
                <div 
                    id="media-container" 
                    className={`media-container ${isPawsAndTales ? 'paws-and-tales-theme' : ''} ${getThemeClass()}`} 
                    style={{
                        display: "flex", 
                        flexDirection: "column",
                        '--vpv-theme-color': customColor,
                        '--vpv-text-color': textColor
                    }}
                >
                    {error && <div className="error">Error: {error.message}</div>}
                    <div id='series-player-container' className={`series-player ${isPawsAndTales ? 'paws-and-tales-theme' : ''} ${getThemeClass()}`}>
                        {playlistItems && playlistItems.length > 0 && (
                            <NewVideoPlayer 
                                playlist={playlistItems} 
                                onPlaylistItemChange={handlePlaylistItemChange}
                                currentPlaylistItem={currentPlaylistItem}
                                isSeries={true}
                                prerollAdEnabled={prerollAdEnabled}
                                postrollAdEnabled={postrollAdEnabled}
                                pauseAdUrl={DEFAULT_PAUSE_AD_URL}
                                pauseAdClickUrl={DEFAULT_PAUSE_AD_CLICK_URL}
                                initialIndex={initialIndex}
                                textColor={textColor}
                            />
                        )}
                    </div>
                    {(!playlistItems || playlistItems.length === 0) && !error && (
                        <div style={{ padding: '20px', textAlign: 'center', color: '#fff' }}>
                            {t.loadingPlayer}
                        </div>
                    )}
                    {parentToolsItem && (
                        <ParentToolsModal
                            item={parentToolsItem}
                            episodeNumber={parentToolsIndex + 1}
                            onClose={() => setParentToolsGuid(null)}
                        />
                    )}
                </div>
            </div>
        </TextExpansionProvider>
    );
}

export default SeriesPlayer;
